import { initializeScrolTop } from 'utils/dom';
import React from 'react';
import { Props as PaginatedListProps } from './PaginatedList';

type ButtonProps =
  | { tag: 'pageNumber'; value: number }
  | { tag: 'canPrev'; prev: () => void }
  | { tag: 'canNext'; next: () => void }
  | {
      tag: 'omitted' | 'cannotPrev' | 'cannotNext';
    };

const getButtonProps = ({
  current,
  next,
  prev,
}: {
  prev: number[];
  current: number;
  next: number[];
}): ButtonProps[] => {
  const wholeLength = prev.length + 1 + next.length;
  if (wholeLength <= 7)
    return [
      ...prev.map((item): ButtonProps => ({ tag: 'pageNumber', value: item })),
      { tag: 'pageNumber', value: current },
      ...next.map((item): ButtonProps => ({ tag: 'pageNumber', value: item })),
    ];
  const thin = (arr: number[]): ButtonProps[] => {
    if (arr.length <= 3)
      return arr.map(item => ({ tag: 'pageNumber', value: item }));
    return arr.flatMap((item, i, arr): ButtonProps[] => {
      if (i === 0 || i === arr.length - 1)
        return [{ tag: 'pageNumber', value: item }];
      if (i === 1) return [{ tag: 'omitted' }];
      return [];
    });
  };
  return [...thin(prev), { tag: 'pageNumber', value: current }, ...thin(next)];
};

export const InvestmentTrustPager: PaginatedListProps['Pager'] = ({
  next,
  prev,
  maxPageNumber,
  selectedPageNumber,
  setSelectedPageNumber,
}) => {
  React.useEffect(() => {
    initializeScrolTop();
  }, [next, prev]);
  const buttonClassName =
    'h-10 w-10 rounded flex flex-row items-center justify-center';
  const buttonProps: ButtonProps[] = [
    prev,
    ...getButtonProps(
      (Array.from({
        length: maxPageNumber,
      }) as number[]).reduce(
        (acc, _, i) => {
          const pageNumber = i + 1;
          if (pageNumber === acc.current) return acc;
          if (pageNumber < acc.current)
            return {
              ...acc,
              prev: [...acc.prev, pageNumber],
            };
          return {
            ...acc,
            next: [...acc.next, pageNumber],
          };
        },
        {
          prev: [],
          current: selectedPageNumber,
          next: [],
        } as {
          prev: number[];
          current: number;
          next: number[];
        }
      )
    ),
    next,
  ];
  if (maxPageNumber <= 1) return <></>;
  return (
    <>
      <div className="flex flex-row items-center justify-center mx-6 mt-8">
        {buttonProps.map(
          // eslint-disable-next-line array-callback-return
          (p, i, arr): React.ReactElement => {
            const ml = i === 0 ? '' : 'ml-2';
            switch (p.tag) {
              case 'canNext':
              case 'canPrev':
              case 'cannotNext':
              case 'cannotPrev':
                const iconDirection =
                  p.tag === 'canNext' || p.tag === 'cannotNext'
                    ? 'right'
                    : 'left';
                const onClick =
                  p.tag === 'canNext'
                    ? p.next
                    : p.tag === 'canPrev'
                    ? p.prev
                    : undefined;
                const disabled =
                  p.tag === 'cannotNext' || p.tag === 'cannotPrev';
                return (
                  <button
                    key={`${i}${p.tag}`}
                    onClick={onClick}
                    disabled={disabled}
                    className={`${buttonClassName} ${ml} ${
                      disabled
                        ? 'text-bluegray-300 cursor-default'
                        : 'text-bluegray-700 cursor-pointer'
                    } text-lg bg-white icon-chevron-${iconDirection} user`}
                    style={{ userSelect: 'none' }}
                  />
                );
              case 'omitted':
                return (
                  <div
                    key={`${i}${p.tag}`}
                    className={`${buttonClassName} bg-transparent text-bluegray-700 ${ml}`}>
                    …
                  </div>
                );
              case 'pageNumber':
                return (
                  <div
                    key={`${i}${p.tag}`}
                    className={`${buttonClassName} ${ml} ${
                      p.value === selectedPageNumber
                        ? 'bg-blue-700 text-white'
                        : 'text-bluegray-700 bg-white'
                    } cursor-pointer`}
                    style={{ userSelect: 'none' }}
                    onClick={(): void => setSelectedPageNumber(p.value)}>
                    {p.value}
                  </div>
                );
            }
          }
        )}
      </div>
    </>
  );
};
